import { useMemo, useState } from 'react';
import { ChevronDown, Pencil, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavigationStore, NavItem } from '../../stores/navigationStore';
import { getQuickActionById } from '../../config/quickActions';
import { QuickActionsEditor } from './QuickActionsEditor';

interface QuickActionsBarProps {
  allNavItems: NavItem[];
}

export function QuickActionsBar({ allNavItems }: QuickActionsBarProps) {
  const { density, incrementUsage, quickActions: rawQuickActions } = useNavigationStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Defensive guard: ensure quickActions is always an array
  const quickActions = Array.isArray(rawQuickActions) ? rawQuickActions : [];
  const navItemsById = useMemo(
    () => new Map(allNavItems.map((item) => [item.id, item])),
    [allNavItems],
  );
  const allowedQuickActions = quickActions.filter((actionId) => navItemsById.has(actionId));

  const buttonSize = {
    compact: 'p-2',
    normal: 'p-2.5',
    comfortable: 'p-3',
  }[density];

  const iconSize = {
    compact: 'w-4 h-4',
    normal: 'w-4 h-4',
    comfortable: 'w-5 h-5',
  }[density];

  return (
    <>
      <div className="group relative overflow-hidden rounded-lg border border-slate-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsExpanded((expanded) => !expanded)}
            className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 transition hover:bg-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
            aria-expanded={isExpanded}
            aria-controls="sidebar-quick-actions"
          >
            <Zap className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="flex-1">Quick Actions</span>
            <span className="rounded-full bg-white/80 px-1.5 py-0.5 text-[10px] text-slate-500">
              {allowedQuickActions.length}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="mr-1 rounded-md p-2 text-slate-400 transition hover:bg-white/70 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            title="Edit Quick Actions"
            aria-label="Edit Quick Actions"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        {isExpanded && (
        <div id="sidebar-quick-actions" className={`grid gap-1.5 border-t border-white/70 p-2 ${allowedQuickActions.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {allowedQuickActions.map((actionId) => {
            const action = getQuickActionById(actionId);
            if (!action) {
              console.warn(`[quick-actions] Action "${actionId}" is not registered in QuickActionsBar.`);
              return null;
            }

            const navItem = navItemsById.get(action.id);
            const Icon = navItem?.icon ?? Zap;

            // Generate a deterministic color based on the ID for consistent UI
            const colors = [
              'text-blue-600 bg-blue-50 hover:bg-blue-100',
              'text-purple-600 bg-purple-50 hover:bg-purple-100',
              'text-green-600 bg-green-50 hover:bg-green-100',
              'text-orange-600 bg-orange-50 hover:bg-orange-100',
              'text-indigo-600 bg-indigo-50 hover:bg-indigo-100',
              'text-rose-600 bg-rose-50 hover:bg-rose-100',
            ];

            // Simple hash function to pick color
            const colorIndex = action.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
            const colorClass = colors[colorIndex];

            return (
              <Link
                key={action.id}
                to={action.path}
                onClick={() => incrementUsage(action.id)}
                className={`flex min-w-0 flex-col items-center justify-center ${buttonSize} rounded-lg shadow-sm transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${colorClass}`}
                title={action.label}
              >
                <Icon className={iconSize} />
                <span className="text-xs font-medium mt-1 truncate w-full px-1">{action.label}</span>
              </Link>
            );
          })}

          {allowedQuickActions.length === 0 && (
            <button
              onClick={() => setIsEditing(true)}
              className="col-span-2 flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 text-gray-400 hover:text-blue-600 transition-all"
            >
              <Zap className="w-5 h-5 mb-1" />
              <span className="text-xs">Setup Quick Actions</span>
            </button>
          )}
        </div>
        )}
      </div>

      <QuickActionsEditor
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        allNavItems={allNavItems}
      />
    </>
  );
}
